import { useEffect, useState } from "react";
import { Users, FileCheck2, Sparkles, Upload, LayoutDashboard, ClipboardList, MessageSquare } from "lucide-react";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ReviewQueue } from "@/components/ReviewQueue";
import { AdminGenerate, AdminUpload, AdminUsers } from "@/pages/admin/AdminSections";
import { AdminFeedback } from "@/pages/admin/AdminFeedback";

const Kpi = ({ label, value }) => (
  <div className="rounded-2xl border border-border bg-card p-5" data-testid={`admin-kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">{label}</div>
    <div className="mt-2 font-heading text-3xl font-bold">{value ?? "—"}</div>
  </div>
);

export default function AdminPortal() {
  const [stats, setStats] = useState({});
  const [tab, setTab] = useState("overview");
  const [jobId, setJobId] = useState(null);
  const [feedbackQid, setFeedbackQid] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);

  const openJob = (id) => { setJobId(id); setTab("review"); };
  const openFeedback = (qid) => { setFeedbackQid(qid); setTab("feedback"); };
  const changeTab = (v) => { if (v !== "review") setJobId(null); if (v !== "feedback") setFeedbackQid(null); setTab(v); };

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6" data-testid="admin-portal">
      <div className="mb-8">
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">Admin</p>
        <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight sm:text-4xl">Control Center</h1>
      </div>

      <Tabs value={tab} onValueChange={changeTab}>
        <TabsList className="mb-6 flex h-auto flex-wrap justify-start gap-1 bg-secondary/60 p-1">
          <TabsTrigger value="overview" data-testid="tab-overview" className="gap-1.5"><LayoutDashboard className="h-4 w-4" /> Overview</TabsTrigger>
          <TabsTrigger value="generate" data-testid="tab-generate" className="gap-1.5"><Sparkles className="h-4 w-4" /> Generate</TabsTrigger>
          <TabsTrigger value="upload" data-testid="tab-upload" className="gap-1.5"><Upload className="h-4 w-4" /> Upload PDF</TabsTrigger>
          <TabsTrigger value="review" data-testid="tab-review" className="gap-1.5"><ClipboardList className="h-4 w-4" /> Review Queue</TabsTrigger>
          <TabsTrigger value="feedback" data-testid="tab-feedback" className="gap-1.5"><MessageSquare className="h-4 w-4" /> Feedback</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users" className="gap-1.5"><Users className="h-4 w-4" /> Users</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
            <Kpi label="Users" value={stats.users} />
            <Kpi label="Students" value={stats.students} />
            <Kpi label="Published" value={stats.published} />
            <Kpi label="Pending Review" value={stats.pending} />
            <Kpi label="AI Generated" value={stats.aiGenerated} />
            <Kpi label="Attempts" value={stats.attempts} />
            <button onClick={() => changeTab("feedback")} className="text-left" data-testid="kpi-feedback-link">
              <Kpi label="New Feedback" value={stats.newFeedback} />
            </button>
          </div>
        </TabsContent>
        <TabsContent value="generate"><AdminGenerate onOpenJob={openJob} /></TabsContent>
        <TabsContent value="upload"><AdminUpload onOpenJob={openJob} /></TabsContent>
        <TabsContent value="review"><ReviewQueue jobId={jobId} onClearJob={() => setJobId(null)} onOpenFeedback={openFeedback} /></TabsContent>
        <TabsContent value="feedback"><AdminFeedback focusQuestionId={feedbackQid} /></TabsContent>
        <TabsContent value="users"><AdminUsers /></TabsContent>
      </Tabs>
    </div>
  );
}
